'use client';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { useMemo } from 'react';
import type { BannerItem } from '@/app/[locale]/(main)/store/components/items/cms/home-banner/types';
import FreeModeSwiper from '@/app/[locale]/(main)/store/components/items/free-mode-swiper';
import { trackEvent } from '@/lib/event-tracking';
import { useIsMobile } from '@/lib/is-mobile';
import { useAuthStore } from '@/store';
import { useAiFoodOrderStore } from '@/store/use-ai-store';

type NewStoreBannerProps = {
  data?: BannerItem[] | undefined;
};

export default function NewStoreBanner({ data }: NewStoreBannerProps) {
  const isMobile = useIsMobile();
  const { user } = useAuthStore();
  const searchParams = useSearchParams();
  const isSearchByCategory = searchParams.get('category_id');
  const query = searchParams.get('q');
  const deal = searchParams.get('campaign_id');
  const category = searchParams.get('category');
  const cityId = useAiFoodOrderStore.use.city_id();

  const localizedType = (cityId: string): string => {
    switch (cityId) {
      case 'SGN':
        return 'aifood-sgn';
      case 'DAD':
        return 'aifood-dad';
      case 'HAN':
        return 'aifood-han';
      default:
        return 'aifood';
    }
  };

  // Đảm bảo luôn lấy ra mảng banner
  const homeBannerRes = useMemo(() => {
    if (Array.isArray(data)) return data;
    // Nếu data là object kiểu { data: { attributes: { items: [...] } } }
    if (
      data &&
      typeof data === 'object' &&
      'data' in data &&
      (data as any).data?.attributes?.items
    ) {
      return (data as any).data.attributes.items;
    }
    return [];
  }, [data]);

  // Filter banners by type
  const homeBannerData = useMemo(
    () => homeBannerRes.filter((e) => e.type === 'aifood'),
    [homeBannerRes]
  );
  const localizedBannerData = useMemo(
    () => homeBannerRes.filter((e) => e.type === localizedType(cityId ?? '')),
    [cityId, homeBannerRes]
  );
  const combinedBannerData = useMemo(() => {
    return [...homeBannerData, ...localizedBannerData];
  }, [homeBannerData, localizedBannerData]);

  const hasActiveFilters = !!(deal ?? query ?? category ?? isSearchByCategory);

  // Only hide if filters are active or no banners
  if (hasActiveFilters || combinedBannerData.length === 0) {
    return null;
  }

  const handleBannerClick = (banner: any) => {
    trackEvent('click_button', {
      button_name: 'click_banner',
      banner_id: banner?.desktop_image?.data?.id ?? '',
      user_id: user?.cunstomerId,
    });
  };

  // Render banners
  return (
    <section className="bg-white py-4 md:py-8 [&+section.ahf-deal]:-mt-10 md:[&+section.ahf-deal]:mt-0">
      <div className="mx-auto w-full max-w-screen-xl px-4 md:px-6">
        <FreeModeSwiper
          enableSingMode={true}
          navigation={false}
          slidesView={2}
          className="w-full !overflow-visible"
          spaceBetween={16}
        >
          {combinedBannerData.length === 0 ? (
            <FreeModeSwiper.Slide
              style={{
                width: isMobile ? '100%' : '49%',
                aspectRatio: '1000/443',
              }}
            ></FreeModeSwiper.Slide>
          ) : (
            combinedBannerData.map((banner, index) => (
              <FreeModeSwiper.Slide
                key={index}
                style={{
                  width: isMobile ? '100%' : '49%',
                  aspectRatio: '1000/443',
                }}
              >
                <Link
                  href={banner?.link ?? ''}
                  onClick={() => handleBannerClick(banner)}
                >
                  <div className="relative size-full overflow-hidden rounded-xl">
                    <div className="p-0">
                      <img
                        src={banner.desktop_image.data.attributes.url}
                        alt={
                          banner.desktop_image.data.id?.toString() || 'Banner'
                        }
                        className="w-full h-auto object-contain rounded-xl"
                        style={{
                          maxHeight: isMobile ? '180px' : '443px',
                          background: '#fff',
                        }}
                      />
                    </div>
                  </div>
                </Link>
              </FreeModeSwiper.Slide>
            ))
          )}
        </FreeModeSwiper>
      </div>
    </section>
  );
}
